import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

export interface InternalLink {
  url: string;
  title: string;
  description?: string;
  priority: 'high' | 'medium' | 'low';
  icon: React.ReactNode;
}

export interface InternalLinksSectionProps {
  links: InternalLink[];
}

export const InternalLinksSection: React.FC<InternalLinksSectionProps> = ({ links }) => {
  const intl = useIntl();

  if (!links || links.length === 0) {
    return null;
  }

  return (
    <div className="bg-white/90 dark:bg-[#1F1509]/95 shadow dark:shadow-none border border-[#DDD5C8]/70 dark:border-[#4A3520]/80 rounded-[28px] px-4 md:px-10 py-10 space-y-6 mt-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
          <FormattedMessage 
            id="jobDetail.similarJobs.title" 
            defaultMessage="Related Opportunities" 
            description="Section heading for similar/related job listings" 
          />
        </h3>
        <div className="w-8 h-8 bg-[#F2EADF] dark:bg-[#2A1D0F] rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-[#3B5E45] dark:text-[#98CFA8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </div>
      </div>
      
      <div className="space-y-3">
        {links.map((link, index) => (
          <a
            key={index}
            href={link.url}
            className={`group flex items-center justify-between p-4 rounded-2xl transition-colors duration-300 ${
              link.priority === 'high'
                ? 'bg-[#F5EDD0]/90 dark:bg-[#2A1E08]/90 border border-[#B8860B]/25 dark:border-[#4A3520] hover:bg-[#F5EDD0] dark:hover:bg-[#342415]'
                : link.priority === 'medium'
                ? 'bg-[#C8DDCE]/50 dark:bg-[#1A2E22]/90 border border-[#3B5E45]/25 dark:border-[#4A3520] hover:bg-[#C8DDCE]/70 dark:hover:bg-[#342415]'
                : 'bg-[#F2EADF]/90 dark:bg-[#2A1D0F]/95 border border-[#DDD5C8]/80 dark:border-[#4A3520] hover:bg-[#EDE3D4] dark:hover:bg-[#342415]'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-lg shadow-sm dark:shadow-none ${
                link.priority === 'high'
                  ? 'bg-[#F5EDD0] dark:bg-[#342415]'
                  : link.priority === 'medium'
                  ? 'bg-[#C8DDCE] dark:bg-[#1A2E22]'
                  : 'bg-[#DDD5C8] dark:bg-[#2A1D0F]'
              }`}>
                {link.icon}
              </div>
              <div>
                <span className={`text-sm font-medium ${
                  link.priority === 'high'
                    ? 'text-[#6E4F00] dark:text-[#D4A820]'
                    : link.priority === 'medium'
                    ? 'text-[#3B5E45] dark:text-[#98CFA8]'
                    : 'text-[#1E1A14] dark:text-[#F2EADF]'
                }`}>
                  {link.title}
                </span>
                {link.description && (
                  <div className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] mt-0.5">
                    {link.description}
                  </div>
                )}
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${
              link.priority === 'high'
                ? 'bg-[#DDD5C8] dark:bg-[#6E4F00]/40'
                : link.priority === 'medium'
                ? 'bg-[#C8DDCE] dark:bg-[#3B5E45]/50'
                : 'bg-[#F2EADF] dark:bg-[#4A3520]'
            }`}>
              <svg className="w-3 h-3 text-[#5A3E20] dark:text-[#D4B898]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </a>
        ))}
      </div>
      
      <div className="mt-6 pt-4 border-t border-[#DDD5C8] dark:border-[#4A3520]">
        <p className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] text-center">
          <FormattedMessage 
            id="jobDetail.similarJobs.description" 
            defaultMessage="Discover more opportunities that match your interests and skills"
            description="Description text for similar jobs section explaining the purpose of showing related opportunities" 
          />
        </p>
      </div>
    </div>
  );
};
